import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const POLICY = [
  { test: (s) => s.length >= 10, label: "At least 10 characters" },
  { test: (s) => /[A-Z]/.test(s), label: "An uppercase letter" },
  { test: (s) => /[a-z]/.test(s), label: "A lowercase letter" },
  { test: (s) => /\d/.test(s), label: "A digit" },
  { test: (s) => /[^A-Za-z0-9]/.test(s), label: "A symbol" },
];

export default function AdminChangePassword() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const forced = !!user?.must_change_password;
  const passed = POLICY.map((r) => r.test(next));
  const allPassed = passed.every(Boolean);
  const matches = next.length > 0 && next === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next) return toast.error("Fill both passwords.");
    if (!allPassed) return toast.error("Password does not meet the policy.");
    if (next !== confirm) return toast.error("Passwords do not match.");
    if (current === next) return toast.error("New password must differ from current.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refreshUser();
      toast.success("Password updated.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not change password."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-5 py-10" data-testid="admin-change-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-teal-600/10 text-teal-600 mb-4">
            <ShieldCheck className="h-7 w-7" strokeWidth={1.4} />
          </div>
          <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/55">Security</div>
          <h1 className="font-display text-3xl text-charcoal mt-2">
            {forced ? "Set a new password" : "Change your password"}
          </h1>
          <p className="text-charcoal/65 text-sm mt-3 leading-relaxed">
            {forced
              ? "This is your first sign-in. Replace the temporary password before continuing."
              : "Choose a strong password. You will be redirected to the dashboard once it's saved."}
          </p>
        </div>

        {forced ? (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 mb-5 text-sm" data-testid="force-change-banner">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>Temporary password in use — change required to access the dashboard.</span>
          </div>
        ) : null}

        <form onSubmit={submit} className="bg-white border border-beige p-6 md:p-8 space-y-5" data-testid="change-password-form">
          <PwField label="Current password" value={current} onChange={setCurrent} testid="cp-current" autoFocus />
          <PwField label="New password" value={next} onChange={setNext} testid="cp-new" />
          <ul className="text-[11px] space-y-1 -mt-2">
            {POLICY.map((r, i) => (
              <li key={r.label} className={`flex items-center gap-2 ${passed[i] ? "text-emerald-700" : "text-charcoal/50"}`} data-testid={`policy-rule-${i}`}>
                <span className={`inline-block w-1.5 h-1.5 ${passed[i] ? "bg-emerald-600" : "bg-charcoal/30"}`} />
                {r.label}
              </li>
            ))}
          </ul>
          <PwField label="Confirm new password" value={confirm} onChange={setConfirm} testid="cp-confirm" />
          {confirm.length > 0 && !matches ? (
            <div className="text-[11px] text-rose-700">Passwords do not match.</div>
          ) : null}

          <div className="pt-2 space-y-3">
            <button
              type="submit"
              disabled={busy || !allPassed || !matches}
              data-testid="cp-submit"
              className="btn-primary w-full h-11 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Updating</> : <><Lock className="h-4 w-4" />Update password</>}
            </button>
            {!forced ? (
              <button type="button" onClick={() => navigate(-1)} data-testid="cp-cancel" className="w-full text-[11px] uppercase tracking-[0.22em] text-charcoal/60 hover:text-teal-600 py-2">
                Cancel
              </button>
            ) : null}
          </div>
        </form>

        <p className="mt-6 text-center text-[10px] uppercase tracking-[0.2em] text-charcoal/40">
          Signed in as {user?.email}
        </p>
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, testid, autoFocus }) {
  return (
    <label className="block">
      <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">{label}</span>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        autoComplete={testid === "cp-current" ? "current-password" : "new-password"}
        data-testid={testid}
        className="mt-2 field-input"
      />
    </label>
  );
}
